import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu } from '../../../components/ui/menu';
import EditBoardModal from './EditBoardModal';
import CreateBoardModal from './CreateBoardModal';
import DeleteBoardModal from './DeleteBoardModal';
import { Pencil, Trash2, PlusCircle } from 'lucide-react';
import { useCRM } from '../../../hooks/crm';

function HeaderBoardDropdown() {
  const { t } = useTranslation();
  const {
    boards,
    selectedBoardId,
    selectBoard,
    createBoard,
    renameBoard,
    deleteBoard,
  } = useCRM();

  const [newBoardName, setNewBoardName] = useState('');
  const [modalState, setModalState] = useState({
    isCreating: false,
    isEditing: false,
    isDeleting: false,
    boardIdToEdit: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  const sortedBoards = useMemo(() => {
    return [...boards].sort((left, right) =>
      left.board_name.localeCompare(right.board_name)
    );
  }, [boards]);

  const selectedBoardName = useMemo(() => {
    if (!selectedBoardId) {
      return t('app:crm.t34');
    }

    const board = sortedBoards.find((item) => String(item.board_id) === String(selectedBoardId));
    return board?.board_name || t('app:crm.t34');
  }, [selectedBoardId, sortedBoards, t]);

  const handleCreateBoard = async () => {
    if (!newBoardName.trim()) {
      return;
    }

    setIsLoading(true);
    try {
      await createBoard(newBoardName.trim());
      setModalState((prev) => ({ ...prev, isCreating: false }));
      setNewBoardName('');
    } catch (error) {
      console.error('Error creating board:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenameBoard = async () => {
    if (!newBoardName.trim() || !modalState.boardIdToEdit) {
      return;
    }

    setIsLoading(true);
    try {
      await renameBoard(modalState.boardIdToEdit, newBoardName.trim());
      setModalState((prev) => ({
        ...prev,
        isEditing: false,
        boardIdToEdit: null,
      }));
      setNewBoardName('');
    } catch (error) {
      console.error('Error renaming board:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteBoard = async () => {
    if (!modalState.boardIdToEdit) {
      return;
    }

    setIsLoading(true);
    try {
      await deleteBoard(modalState.boardIdToEdit);
      setModalState((prev) => ({
        ...prev,
        isDeleting: false,
        boardIdToEdit: null,
      }));
    } catch (error) {
      console.error('Error deleting board:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const boardItems = sortedBoards.map((board) => ({
    label: board.board_name,
    onClick: () => selectBoard(board.board_id),
    icons: [
      {
        icon: Pencil,
        onClick: () => {
          setModalState((prev) => ({
            ...prev,
            isEditing: true,
            boardIdToEdit: board.board_id,
          }));
          setNewBoardName(board.board_name);
        },
      },
      {
        icon: Trash2,
        onClick: () => {
          setModalState((prev) => ({
            ...prev,
            isDeleting: true,
            boardIdToEdit: board.board_id,
          }));
        },
      },
    ],
  }));

  const noBoardsItem = {
    label: t('app:crm.t35'),
    disabled: true,
  };

  const createBoardItem = {
    label: t('app:crm.t36'),
    onClick: () => setModalState((prev) => ({ ...prev, isCreating: true })),
    className: 'bg-stone-100 text-stone-950 hover:bg-stone-200 flex items-center',
    icons: [
      {
        icon: PlusCircle,
        onClick: () => {
          setModalState((prev) => ({ ...prev, isCreating: true }));
        },
        iconProps: { className: 'h-4 w-4 text-stone-950' },
      },
    ],
  };

  const tabs = [
    {
      label: selectedBoardName,
      items: sortedBoards.length === 0
        ? [noBoardsItem, createBoardItem]
        : [...boardItems, createBoardItem],
    },
  ];

  return (
    <>
      <Menu tabs={tabs} />

      <CreateBoardModal
        isOpen={modalState.isCreating}
        onClose={() => setModalState((prev) => ({ ...prev, isCreating: false }))}
        onCreate={handleCreateBoard}
        newBoardName={newBoardName}
        setNewBoardName={setNewBoardName}
        isLoading={isLoading}
        t={t}
      />

      <EditBoardModal
        isOpen={modalState.isEditing}
        onClose={() => setModalState((prev) => ({
          ...prev,
          isEditing: false,
          boardIdToEdit: null,
        }))}
        onRename={handleRenameBoard}
        newBoardName={newBoardName}
        setNewBoardName={setNewBoardName}
        isLoading={isLoading}
        t={t}
      />

      {modalState.isDeleting && (
        <DeleteBoardModal
          isOpen={modalState.isDeleting}
          boardName={
            boards.find((board) => String(board.board_id) === String(modalState.boardIdToEdit))?.board_name
          }
          onConfirm={handleDeleteBoard}
          onCancel={() => setModalState((prev) => ({
            ...prev,
            isDeleting: false,
            boardIdToEdit: null,
          }))}
        />
      )}
    </>
  );
}

export default React.memo(HeaderBoardDropdown);
